import { Button, ScrollView, Text, View, XStack } from "tamagui";
import { Loader } from "../../../../components/design-system/Loader";
import { EventCard } from "../../../../components/design-system/EventCard";
import { brandShadow } from "../../../../../tamagui.config";
import {
  NavigationProp,
  useIsFocused,
  useNavigation,
} from "@react-navigation/native";
import { useCallback, useEffect, useState } from "react";
import { RefreshControl } from "react-native";
import { api, useApiQueryClient } from "../../../../../lib/api";
import { supabase } from "../../../../../lib/supabase";
import { currentUserAtom } from "../../../../../atoms/global";
import { useAtomValue } from "jotai";
import { iProfile } from "../../../../features/Profile";
import { useToast } from "../../../../hooks/useToast";
import { BaseNavigatorParamList } from "../../../../navigators/BaseNavigator";
import { Event } from "../../../../hooks/useGetEvent";

type Invite = {
  id: string;
  event: Event;
  status: string;
  user: iProfile;
};

export const MyInvites = () => {
  const toast = useToast();
  const queryClient = useApiQueryClient();
  const [invites, setInvites] = useState<Invite[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acceptingEventIds, setAcceptingEventIds] = useState<Set<string>>(new Set());

  const isFocused = useIsFocused();

  const user = useAtomValue(currentUserAtom);

  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const removeInviteByEventId = useCallback((eventId: string) => {
    setInvites(prev => prev?.filter(i => String(i.event?.id ?? "") !== eventId) ?? []);
  }, []);

  const fetchInvites = useCallback(async () => {
    const { data, error } = await supabase
      .from("invites")
      .select(
        "*, event:events(*, route:routes(*), host:profiles(*)), user:profiles(*)"
      )
      .eq("recipient_id", user?.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Couldn't load invites",
        haptic: "error",
      });
      setIsLoading(false);
      return;
    }

    const invitesMap = data?.map((i) => ({
      id: i.id,
      user: i.user as iProfile,
      event: i.event as Event,
      status: "pending",
    }));

    // One card per event (handles legacy duplicate rows from repeated sends)
    const byEventId = new Map<string, Invite>();
    for (const inv of invitesMap ?? []) {
      const eid = inv.event?.id;
      if (eid == null || eid === "") continue;
      const key = String(eid);
      if (!byEventId.has(key)) byEventId.set(key, inv);
    }

    setInvites([...byEventId.values()]);
    setIsLoading(false);
  }, [user?.id, toast]);

  useEffect(() => {
    if (isFocused) {
      fetchInvites();
    }
  }, [isFocused, fetchInvites]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchInvites();
    setRefreshing(false);
  }, [fetchInvites]);

  const acceptInvite = async (invite: Invite) => {
    if (!user?.id || invite.event?.id == null || invite.event.id === "") {
      return;
    }
    const eventId = String(invite.event.id);
    setAcceptingEventIds(prev => new Set(prev).add(eventId));
    try {
      await api.post(`/api/v1/events/${eventId}/join`, {});
      queryClient.invalidateQueries({ queryKey: ["events-attending"] });
      toast({
        title: "Invite Accepted",
        haptic: "success",
      });
      removeInviteByEventId(eventId);
      navigation.navigate("EventDetail", {
        eventId,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : "";
      if (message === "Already joined this event") {
        queryClient.invalidateQueries({ queryKey: ["events-attending"] });
        toast({
          title: "You're already on this ride",
          haptic: "success",
        });
        removeInviteByEventId(eventId);
        navigation.navigate("EventDetail", { eventId });
      } else {
        toast({
          title: "Couldn't accept invite",
          haptic: "error",
        });
      }
    } finally {
      setAcceptingEventIds(prev => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
    }
  };

  const declineInvite = async (invite: Invite) => {
    if (!user?.id || invite.event?.id == null || invite.event.id === "") {
      return;
    }
    const { error } = await supabase
      .from("invites")
      .update({ status: "declined" })
      .eq("event_id", invite.event.id)
      .eq("recipient_id", user.id)
      .eq("status", "pending");

    if (error) {
      toast({
        title: "Couldn't decline invite",
        haptic: "error",
      });
    } else {
      toast({
        title: "Invite Declined",
        haptic: "error",
      });
      removeInviteByEventId(String(invite.event.id));
    }
  };

  if (isLoading) {
    return (
      <View flex={1} bg="$brandParchment" justifyContent="center" alignItems="center">
        <Loader />
      </View>
    );
  }

  if (!invites || invites.length === 0) {
    return (
      <ScrollView
        flex={1}
        bg="$brandParchment"
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View
          flex={1}
          justifyContent="center"
          alignItems="center"
          minHeight={300}
          gap="$4"
        >
          <Text fontSize={20}>{`No pending invites`}</Text>
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      px="$4"
      flex={1}
      bg="$brandParchment"
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
      }
    >
      {invites?.map((i) => (
        <View mb="$4" key={i.id}>
          <EventCard event={i.event} />
          <XStack gap="$2" mt="$2">
            <Button
              size="$3"
              fontSize={12}
              bg="$red10Dark"
              color="white"
              fontFamily={"$heading"}
              flex={1}
              pressStyle={{
                bg: "$red10Dark",
                shadowColor: "$colorTransparent",
                shadowOffset: { width: 0, height: 0 },
                borderWidth: 1,
                borderColor: "black",
                scale: 0.98,
              }}
              borderWidth={1}
              borderColor={"black"}
              onPress={() => declineInvite(i)}
              {...brandShadow}
            >
              DECLINE
            </Button>

            <Button
              size="$3"
              fontSize={12}
              bg="$green10Light"
              borderWidth={1}
              borderColor="black"
              color="white"
              flex={1}
              fontFamily={"$heading"}
              disabled={acceptingEventIds.has(String(i.event?.id ?? ""))}
              opacity={
                acceptingEventIds.has(String(i.event?.id ?? "")) ? 0.6 : 1
              }
              onPress={() => acceptInvite(i)}
              pressStyle={{
                bg: "$brandYellow",
                shadowColor: "$colorTransparent",
                shadowOffset: { width: 0, height: 0 },
                borderWidth: 1,
                borderColor: "black",
                scale: 0.98,
              }}
              {...brandShadow}
            >
              ACCEPT
            </Button>
          </XStack>
          <View bg="black" h={1} w="100%" mt="$4" />
        </View>
      ))}
    </ScrollView>
  );
};
